import { useState, useEffect, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { api } from "../api";
import { ROLE_LABELS } from "@shared/types";
import { permittedNav, applyUserPrefs, arrangeNav, moveNavItem, MOBILE_PRIORITY, DEFAULT_ACCENT, readableOn, textColorOnWhite, type NavItem, type NavCustom } from "@shared/nav";
import { buildSearchEntries } from "@shared/nav-search";
import { Icon } from "./icons";
import { NotificationBell } from "./NotificationBell";
import UpdateBanner from "./UpdateBanner";
import NavSearch from "./NavSearch";
import NavPrefsEditor from "./NavPrefsEditor";
import { NavPrefsProvider, useNavPrefsState } from "../lib/navPrefs";
import { useNavDrag } from "../lib/navDrag";

// メニューの定義・表示の決め方は shared/nav.ts（設定画面などはここから import している）
export { NAV_GROUPS, type NavItem, type NavGroup, type NavCustom } from "@shared/nav";

export default function Layout({ children }: { children: ReactNode }) {
  const { user, logout, hasCap, hasRole } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [navCustom, setNavCustom] = useState<NavCustom>({});
  // サイドメニューの折りたたみ（アイコンのみ表示）。状態はブラウザに記憶。
  const [collapsed, setCollapsed] = useState<boolean>(() => { try { return localStorage.getItem("nav_collapsed") === "1"; } catch { return false; } });
  const toggleCollapsed = () => setCollapsed((v) => { const nv = !v; try { localStorage.setItem("nav_collapsed", nv ? "1" : "0"); } catch { /* ignore */ } return nv; });

  useEffect(() => {
    api.get<{ items: NavCustom }>("/nav-customizations").then((d) => setNavCustom(d.items ?? {})).catch(() => {});
  }, []);

  const prefsState = useNavPrefsState(user?.id);
  const [themeOpen, setThemeOpen] = useState(false);   // 表示・テーマ（自分の画面）
  const [acctOpen, setAcctOpen] = useState(false);     // アカウントメニュー
  const [searchOpen, setSearchOpen] = useState(false); // スマホの検索窓

  // 権限（税理士は給与計算のみ）→ 組織のカスタマイズ（全員）→ 自分の画面の非表示 の順に反映。
  // デスクトップ・スマホのメニュー・下部タブ・検索がすべて同じ結果を使う（shared/nav.ts）。
  const viewer = { hasCap, hasRole };
  const available = permittedNav(viewer, navCustom);
  const groups = applyUserPrefs(available, prefsState.prefs);
  const items = groups.flatMap((g) => g.items);
  // 検索は自分で非表示にした画面も対象（メニューから隠しても検索で開ける）
  const searchList = buildSearchEntries(available, viewer);
  const visibleTo = new Set(available.flatMap((g) => g.items.map((i) => i.to)));
  const onSearchAction = (a: "theme" | "password") => (a === "theme" ? setThemeOpen(true) : setPwOpen(true));

  // サイドメニューの色（自分の画面）。選択中の項目の背景と文字、ホバー、下部タブの文字色。
  const accent = prefsState.prefs.accent ?? DEFAULT_ACCENT;
  const navVars = {
    "--nav-accent": accent,
    "--nav-accent-fg": readableOn(accent),
    "--nav-hover": accent === DEFAULT_ACCENT ? "#f0fdf4" : `${accent}1f`,
    "--nav-accent-text": textColorOnWhite(accent),
  } as React.CSSProperties;
  // 並び替え（PCはドラッグ・スマホは長押し）と、グループの折りたたみ。どちらも自分の画面の設定として保存。
  const { pathname } = useLocation();
  const isActivePath = (to: string) => (to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`));
  const folded = new Set(prefsState.prefs.folded ?? []);
  const toggleFold = (id: string) => prefsState.update((p) => {
    const f = new Set(p.folded ?? []);
    if (f.has(id)) f.delete(id); else f.add(id);
    return { ...p, folded: [...f] };
  });
  const drag = useNavDrag((key, t) => prefsState.update((p) => ({ ...p, layout: moveNavItem(arrangeNav(available, p), key, t.group, t.before) })), true);
  const dragLabel = drag.dragKey ? items.find((i) => i.to === drag.dragKey)?.label : null;

  const itemIcon = (i: NavItem, active: boolean) => {
    const color = prefsState.prefs.items?.[i.to]?.color;
    return <span className="shrink-0" style={!active && color ? { color } : undefined}><Icon name={i.icon} className="h-5 w-5" /></span>;
  };

  // サイドメニュー本体（PCのサイドバーとスマホのメニューで同じものを使う）
  const renderNav = (mini: boolean, onNavigate?: () => void) => groups.map((g) => {
    const isFolded = !mini && !!g.label && folded.has(g.id);
    // 折りたたんでいても、いま開いている画面の項目は見せる（どこにいるか分かるように）
    const shown = isFolded ? g.items.filter((i) => isActivePath(i.to)) : g.items;
    const dropAtEnd = drag.target?.group === g.id && drag.target.before === null;
    return (
      <div key={g.id} className={`space-y-1 rounded-lg ${dropAtEnd ? "pb-1 shadow-[inset_0_-2px_0_0_var(--nav-accent)]" : ""}`}>
        {!mini && g.label && (
          <button
            type="button"
            data-nav-group-head={g.id}
            data-nav-first={g.items[0]?.to ?? ""}
            onClick={() => toggleFold(g.id)}
            aria-expanded={!isFolded}
            title={isFolded ? "開く" : "折りたたむ"}
            className="flex w-full items-center justify-between rounded px-3 pt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-600"
          >
            <span>{g.label}{isFolded && <span className="ml-1 font-normal">（{g.items.length}）</span>}</span>
            <span aria-hidden className="text-[10px]">{isFolded ? "▸" : "▾"}</span>
          </button>
        )}
        {shown.map((i) => {
          const next = g.items[g.items.indexOf(i) + 1]?.to ?? "";
          const lineTop = drag.target?.group === g.id && drag.target.before === i.to && drag.dragKey !== i.to;
          const dnd = {
            "data-nav-key": i.to, "data-nav-group": g.id, "data-nav-next": next,
            draggable: false,
            onDragStart: (e: React.DragEvent) => e.preventDefault(),
            onPointerDown: (e: React.PointerEvent) => drag.onPointerDown(e, i.to),
          };
          const extra = `select-none [-webkit-touch-callout:none] ${drag.dragKey === i.to ? "opacity-40" : ""} ${lineTop ? "shadow-[inset_0_2px_0_0_var(--nav-accent)]" : ""}`;
          return i.external ? (
            <a key={i.to} href={i.to} target="_blank" rel="noreferrer" {...dnd} className={`${navLinkCls(mini)({ isActive: false })} ${extra}`} title={mini ? i.label : undefined} onClick={onNavigate}>
              {itemIcon(i, false)}
              {!mini && <span className="flex items-center gap-1">{i.label}<span className="text-[10px] opacity-60">↗</span></span>}
            </a>
          ) : (
            <NavLink key={i.to} to={i.to} {...dnd} className={(st) => `${navLinkCls(mini)(st)} ${extra}`} end={i.to === "/"} title={mini ? i.label : undefined} onClick={onNavigate}>
              {({ isActive }) => <>
                {itemIcon(i, isActive)}
                {!mini && <span>{i.label}</span>}
              </>}
            </NavLink>
          );
        })}
      </div>
    );
  });
  const tabItems = MOBILE_PRIORITY
    .map((to) => items.find((i) => i.to === to))
    .filter((i): i is NavItem => !!i)
    .slice(0, 4);

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const navLinkCls = (mini: boolean) => ({ isActive }: { isActive: boolean }) =>
    `flex items-center rounded-lg py-2 text-sm font-medium transition ${mini ? "justify-center px-2" : "gap-2.5 px-3"} ${
      isActive ? "bg-[color:var(--nav-accent)] text-[color:var(--nav-accent-fg)] shadow-sm" : "text-gray-700 hover:bg-[color:var(--nav-hover)]"
    }`;

  return (
    <NavPrefsProvider value={{ ...prefsState, available }}>
    <div className="min-h-full" style={navVars}>
      {/* top bar */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <button className="sm:hidden" onClick={() => setOpen(!open)} aria-label="メニューを開く">
            <Icon name="menu" className="h-6 w-6 text-gray-600" />
          </button>
          <span className="text-lg font-bold text-brand-700">LocoBridge</span>
        </div>
        <div className="mx-4 hidden max-w-xl flex-1 md:block">
          <NavSearch entries={searchList} canSearchDeals={visibleTo.has("/deals")} canSearchCustomers={visibleTo.has("/customers")} onAction={onSearchAction} shortcut />
        </div>
        <div className="flex items-center gap-3">
          <button className="p-1 text-gray-500 hover:text-brand-600 md:hidden" onClick={() => setSearchOpen(true)} aria-label="機能・画面を検索">
            <Icon name="search" className="h-5 w-5" />
          </button>
          <div className="relative hidden sm:block">
            <button
              className="text-sm text-gray-600 underline-offset-2 hover:text-brand-600 hover:underline"
              onClick={() => setAcctOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={acctOpen}
            >
              {user?.name}（{user?.roles.map((r) => ROLE_LABELS[r]).join("/")}）▾
            </button>
            {acctOpen && <>
              <div className="fixed inset-0 z-30" onClick={() => setAcctOpen(false)} aria-hidden="true" />
              <div role="menu" className="absolute right-0 z-40 mt-2 w-60 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg" onKeyDown={(e) => { if (e.key === "Escape") setAcctOpen(false); }}>
                <button role="menuitem" className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-gray-50" onClick={() => { setAcctOpen(false); setThemeOpen(true); }}>
                  <Icon name="adjust" className="h-4 w-4 text-gray-500" />表示・テーマ（自分の画面）
                </button>
                <button role="menuitem" className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-gray-50" onClick={() => { setAcctOpen(false); setPwOpen(true); }}>
                  <Icon name="cog" className="h-4 w-4 text-gray-500" />パスワード変更
                </button>
              </div>
            </>}
          </div>
          <NotificationBell />
          <button className="btn-secondary" onClick={doLogout}>ログアウト</button>
        </div>
      </header>

      <div className="flex w-full">
        {/* sidebar (desktop) — ヘッダー直下に固定し、メニューだけ独立スクロール。折りたたみ可 */}
        <aside data-nav-scroll className={`sticky top-16 hidden h-[calc(100vh-4rem)] shrink-0 self-start overflow-y-auto overflow-x-hidden border-r border-gray-200 p-3 transition-[width] duration-200 sm:block ${collapsed ? "w-16" : "w-56"}`}>
          <div className={`mb-2 flex ${collapsed ? "justify-center" : "justify-end"}`}>
            <button
              onClick={toggleCollapsed}
              title={collapsed ? "メニューを開く" : "メニューをたたむ"}
              aria-label={collapsed ? "メニューを開く" : "メニューをたたむ"}
              className="rounded-lg px-2 py-1 text-gray-400 hover:bg-brand-50 hover:text-brand-600"
            >
              <span className="text-base leading-none">{collapsed ? "»" : "«"}</span>
            </button>
          </div>
          <nav className="space-y-3" {...drag.containerProps}>
            {renderNav(collapsed)}
          </nav>
          {!collapsed && <p className="mt-4 px-3 text-[10px] leading-relaxed text-gray-400">並び替え：項目をドラッグ<br />見出しを押すと折りたたみ</p>}
        </aside>

        {/* mobile drawer */}
        {open && (
          <div className="fixed inset-0 z-40 sm:hidden" onClick={() => setOpen(false)}>
            <div className="absolute inset-0 bg-black/30" />
            <nav data-nav-scroll className="absolute left-0 top-0 h-full w-72 space-y-1 overflow-y-auto bg-white p-3 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-lg font-bold text-brand-700">LocoBridge</span>
                <button onClick={() => setOpen(false)} aria-label="メニューを閉じる"><Icon name="close" className="h-6 w-6 text-gray-400" /></button>
              </div>
              <div className="space-y-2" {...drag.containerProps}>
                {renderNav(false, () => setOpen(false))}
              </div>
              <p className="px-3 pt-2 text-[10px] text-gray-400">並び替え：項目を長押しして移動／見出しで折りたたみ</p>
              <button
                className="mt-2 flex w-full items-center gap-2.5 rounded-lg border-t border-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-brand-50"
                onClick={() => { setOpen(false); setThemeOpen(true); }}
              >
                <Icon name="adjust" className="h-5 w-5 shrink-0" />
                <span>表示・テーマ（自分の画面）</span>
              </button>
              <button
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-brand-50"
                onClick={() => { setOpen(false); setPwOpen(true); }}
              >
                <Icon name="cog" className="h-5 w-5 shrink-0" />
                <span>パスワード変更</span>
              </button>
            </nav>
          </div>
        )}

        {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}

        {/* ドラッグ中の項目名（指やマウスの横に出す） */}
        {drag.dragKey && drag.pos && dragLabel && (
          <div className="pointer-events-none fixed z-[60] rounded-lg px-3 py-1.5 text-sm font-medium shadow-lg"
            style={{ left: drag.pos.x + 14, top: drag.pos.y - 18, backgroundColor: "var(--nav-accent)", color: "var(--nav-accent-fg)" }} aria-hidden="true">
            ⇅ {dragLabel}
          </div>
        )}

        {/* 表示・テーマ（自分の画面） */}
        {themeOpen && (
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setThemeOpen(false)}>
            <div className="my-6 w-full max-w-3xl rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="theme-title">
              <div className="mb-4 flex items-center justify-between">
                <h2 id="theme-title" className="font-semibold">表示・テーマ（自分の画面）</h2>
                <button onClick={() => setThemeOpen(false)} aria-label="閉じる"><Icon name="close" className="h-5 w-5 text-gray-400" /></button>
              </div>
              <NavPrefsEditor />
            </div>
          </div>
        )}

        {/* スマホの検索窓 */}
        {searchOpen && (
          <div className="fixed inset-0 z-50 bg-black/30 md:hidden" onClick={() => setSearchOpen(false)}>
            <div className="bg-white p-3 shadow-lg" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start gap-2">
                <NavSearch entries={searchList} canSearchDeals={visibleTo.has("/deals")} canSearchCustomers={visibleTo.has("/customers")} onAction={onSearchAction} autoFocus onDone={() => setSearchOpen(false)} />
                <button className="shrink-0 px-1 py-2 text-sm text-gray-500" onClick={() => setSearchOpen(false)}>閉じる</button>
              </div>
            </div>
          </div>
        )}

        {/* content — overflow-x-hidden で、画面幅を超える要素があっても横スクロール（見切れ）を防ぐ。
            幅広の表は各ページで overflow-x-auto の枠を持つため内部スクロールされる。 */}
        {/* overflow-x-clip: 横はみ出しは防ぐが hidden と違いスクロール枠を作らないので、
            ページ内の position:sticky（例: 営業報告の「一覧に戻る」）が正しく効く。 */}
        <main className="min-w-0 flex-1 overflow-x-clip p-4 pb-24 sm:pb-8">{children}</main>
        <UpdateBanner />
      </div>

      {/* mobile bottom tab bar (Sales Hub 風)
          transform-gpu + will-change: iOSのモメンタムスクロール中、position:fixed が再描画されず
          途中位置に取り残されて「移動して見える」のを防ぐ（独立レイヤーに載せて追従させる）。 */}
      <nav style={{ transform: "translateZ(0)", willChange: "transform" }} className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)] sm:hidden">
        {tabItems.map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.to === "/"}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium ${
                isActive ? "text-[color:var(--nav-accent-text)]" : "text-gray-500"
              }`
            }
          >
            {({ isActive }) => <>
              {itemIcon(i, isActive)}
              <span className="leading-none">{i.label}</span>
            </>}
          </NavLink>
        ))}
        <button
          onClick={() => setOpen(true)}
          className="flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium text-gray-500"
        >
          <Icon name="menu" className="h-5 w-5" />
          <span className="leading-none">メニュー</span>
        </button>
      </nav>

      <footer className="border-t border-gray-200 bg-white px-4 py-3 pb-24 text-center text-[11px] leading-relaxed text-gray-400 sm:pb-3">
        本システムは業務管理を補助するツールです。税務・法務・補助金採択可否・会計の正確性を保証しません。最終判断は専門家にご確認ください。
      </footer>
    </div>
    </NavPrefsProvider>
  );
}

// 自分のパスワード変更（全ユーザー利用可）
function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(""); setErr("");
    if (nw.length < 10) { setErr("新しいパスワードは10文字以上にしてください"); return; }
    if (nw !== confirm) { setErr("新しいパスワード（確認）が一致しません"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      setCur(""); setNw(""); setConfirm("");
      setMsg("パスワードを変更しました");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "変更に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative w-full max-w-sm rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">パスワード変更</h2>
          <button onClick={onClose} aria-label="閉じる"><Icon name="close" className="h-5 w-5 text-gray-400" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <label className="block text-sm">現在のパスワード
            <input className="input" type="password" value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" required />
          </label>
          <label className="block text-sm">新しいパスワード（10文字以上）
            <input className="input" type="password" value={nw} onChange={(e) => setNw(e.target.value)} autoComplete="new-password" required minLength={10} />
          </label>
          <label className="block text-sm">新しいパスワード（確認）
            <input className="input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
          </label>
          {err && <p className="text-sm text-red-600">{err}</p>}
          {msg && <p className="text-sm text-brand-600">{msg}</p>}
          <div className="flex gap-2">
            <button className="btn-primary" disabled={busy}>{busy ? "変更中…" : "変更する"}</button>
            <button type="button" className="btn-secondary" onClick={onClose}>閉じる</button>
          </div>
          <p className="text-xs text-gray-400">変更すると、他の端末のログインは無効になります（この端末は維持されます）。</p>
        </form>
      </div>
    </div>
  );
}
